import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserPlus, AlertTriangle, CheckCircle2, ShieldAlert } from "lucide-react";
import LSSDLogo from "../components/LSSDLogo";
import GoogleSignInButton from "../components/GoogleSignInButton";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", rp_name: "", discord_id: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      setSuccess(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Errore di registrazione");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-[var(--lssd-olive)] border-[var(--lssd-khaki)]/30 text-[var(--lssd-cream)] placeholder:text-[var(--lssd-cream)]/40 rounded-2xl h-11";

  return (
    <div className="min-h-[calc(100vh-120px)] bg-[var(--lssd-olive-deep)] flex items-center justify-center px-4 py-16" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="rounded-3xl bg-[var(--lssd-olive-dark)] border border-[var(--lssd-khaki)]/25 p-8 sm:p-10">
          <div className="flex flex-col items-center text-center mb-8">
            <LSSDLogo className="h-16 w-16 mb-4" />
            <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[var(--lssd-khaki)]">
              Richiesta Accesso
            </div>
            <h1 className="font-display font-black text-3xl tracking-tight text-[var(--lssd-cream)] mt-2">
              Nuovo Operatore
            </h1>
            <p className="text-[var(--lssd-cream)]/60 text-sm mt-2">
              La tua registrazione sarà verificata dal Comando.
            </p>
          </div>

          {success ? (
            <div className="text-center" data-testid="register-success">
              <CheckCircle2 className="h-14 w-14 text-[var(--lssd-khaki)] mx-auto mb-4" />
              <h3 className="font-display font-bold text-xl text-[var(--lssd-cream)] mb-2">
                Registrazione Inviata
              </h3>
              <p className="text-[var(--lssd-cream)]/65 text-sm mb-6">
                Il Comando verificherà la tua identità nel dipartimento.
                Riceverai accesso dopo l&apos;approvazione.
              </p>
              <Button
                onClick={() => nav("/login")}
                className="bg-[var(--lssd-khaki)] hover:bg-[var(--lssd-khaki-light)] text-[var(--lssd-olive-deep)] rounded-full font-semibold uppercase tracking-[0.16em] h-11 px-6"
              >
                Vai al Login
              </Button>
            </div>
          ) : (
            <>
              <div className="mb-5">
                <GoogleSignInButton label="Registrati con Google" testId="register-google-btn" />
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex-1 h-px bg-[var(--lssd-khaki)]/20" />
                  <span className="uppercase tracking-[0.2em] text-[10px] text-[var(--lssd-khaki)]/70">
                    oppure email
                  </span>
                  <div className="flex-1 h-px bg-[var(--lssd-khaki)]/20" />
                </div>
              </div>
              <form onSubmit={submit} className="space-y-4" data-testid="register-form">
                <div
                  className="flex items-start gap-2 p-3 bg-[var(--lssd-khaki)]/10 border border-[var(--lssd-khaki)]/35 rounded-2xl text-[12px] text-[var(--lssd-cream)] leading-relaxed"
                  data-testid="register-real-data-warning"
                >
                  <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0 text-[var(--lssd-khaki)]" />
                  <span>
                    <span className="font-semibold text-[var(--lssd-khaki)]">Attenzione:</span> inserisci
                    esclusivamente <span className="font-semibold">dati reali</span> — email valida,
                    ID Discord numerico corretto e una password sicura.
                  </span>
                </div>

                <div>
                  <Label className="text-xs font-semibold text-[var(--lssd-cream)]/85 mb-1.5 block">Nome e Cognome RP</Label>
                  <Input data-testid="register-rp-name" required minLength={2} value={form.rp_name} onChange={(e) => setForm({ ...form, rp_name: e.target.value })} placeholder="John Marston" className={inputCls} />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[var(--lssd-cream)]/85 mb-1.5 block">Email</Label>
                  <Input data-testid="register-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="andrearossi@gmail.com" className={inputCls} />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[var(--lssd-cream)]/85 mb-1.5 block">Password</Label>
                  <Input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Minimo 6 caratteri" className={inputCls} />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[var(--lssd-cream)]/85 mb-1.5 block">
                    ID Discord
                    <span className="ml-1 text-[10px] text-[var(--lssd-cream)]/45 font-normal">(per sincronizzare il grado sul server)</span>
                  </Label>
                  <Input
                    data-testid="register-discord-id"
                    type="text"
                    required
                    pattern="\d{15,25}"
                    inputMode="numeric"
                    value={form.discord_id}
                    onChange={(e) => setForm({ ...form, discord_id: e.target.value.replace(/\D/g, "") })}
                    placeholder="es. 351876452198482432"
                    className={inputCls}
                  />
                  <p className="text-[10px] text-[var(--lssd-cream)]/45 mt-1.5 leading-snug">
                    Su Discord: <span className="text-[var(--lssd-khaki)]">Impostazioni → Avanzate → Modalità sviluppatore ON</span>,
                    poi click destro sul tuo nome → <span className="text-[var(--lssd-khaki)]">Copia ID utente</span>.
                  </p>
                </div>

                {error && (
                  <div data-testid="register-error" className="flex items-start gap-2 p-3 bg-red-500/15 border border-red-500/40 rounded-2xl text-sm text-red-200">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="register-submit"
                  className="w-full bg-[var(--lssd-khaki)] hover:bg-[var(--lssd-khaki-light)] text-[var(--lssd-olive-deep)] rounded-full font-semibold uppercase tracking-[0.16em] h-11 transition-colors"
                >
                  {loading ? "Invio..." : (<><UserPlus className="h-4 w-4 mr-2" /> Richiedi Accesso</>)}
                </Button>
              </form>
            </>
          )}

          <div className="mt-6 pt-6 border-t border-[var(--lssd-khaki)]/15 text-center text-sm">
            <span className="text-[var(--lssd-cream)]/55">Hai già un account?</span>{" "}
            <Link to="/login" className="text-[var(--lssd-khaki)] font-semibold hover:text-[var(--lssd-cream)] transition-colors" data-testid="register-to-login">
              Accedi
            </Link>
          </div>
        </div>
        <p className="text-center text-[10px] uppercase tracking-[0.24em] text-[var(--lssd-khaki)]/60 mt-6">
          A Tradition of Service Since 1852
        </p>
      </div>
    </div>
  );
}
